/**
 * 音声認識の文字（Issue #784。索引 #775）: TetherCam の設定のページで、席ごとに今聞こえている途中の文字と、確定した文字を分けて出す。
 * - 途中の欄: 補正サーバーの GET /live/speech/watch?channel= の final でない行（確定したら消える）
 * - 確定の欄: 同じ知らせの final（その席に流したもの）と、GET /live/speech/recent の判定（2 秒ごと）のうち捨てたもの（灰色）。新しいものが上
 *   行ごとに聞こえた元（iPhone のマイク / OBS のトラック）・大きさ・判定（core/live/speech.py の decide。同席の 2 台の突き合わせと下限）
 * 補正サーバーは収録のときだけ起きているので、「聞く」を押したときだけ開く。
 */
import { liveUrl } from './liveBase';
import { useEffect, useRef, useState } from 'react';
import { splitLines } from './RecogPanel';

const MAX_FINALS = 30;
const POLL_MS = 2000;

export const sourceText = (audio) => (audio === 'obs' ? 'OBS のトラック' : 'マイク');

const db = (v) => (v == null ? '?' : `${Number(v).toFixed(1)} dB`);

/** 判定 → 文（attribution / decisions の 1 件。thresholds は recent の margin_db / min_level_db） */
export function judgeText(att, thresholds = {}) {
  if (!att) return 'この席（大きさなし）';
  const kind = att.audio === 'obs' ? 'obs' : 'mic';
  const margin = thresholds.margin_db?.[kind];
  const floor = thresholds.min_level_db?.[kind];
  switch (att.decided) {
    case 'louder': return `この席（相手より ${Number(att.diff_db).toFixed(1)} dB 大きい）`;
    case 'alone': return 'この席（相手の席では聞こえていない）';
    case 'other_seat': return `捨てた: 相手の席の声（相手 ${db(att.other_db)}）`;
    case 'ambiguous': return `捨てた: 差が小さい（${Number(att.diff_db).toFixed(1)} dB${margin != null ? `。${margin} dB 未満` : ''}）`;
    case 'quiet': return `捨てた: 小さすぎる（${floor != null ? `下限 ${floor} dB` : '下限より下'}）`;
    default: return att.decided ?? '';
  }
}

export function thresholdText(t) {
  if (!t?.margin_db) return '';
  const one = (k, label) => `${label} 差 ${t.margin_db[k]} dB・下限 ${t.min_level_db?.[k] == null ? 'なし' : `${t.min_level_db[k]} dB`}`;
  return `しきい値: ${one('mic', 'マイク')} / ${one('obs', 'OBS')}`;
}

const byTime = (a, b) => (b.t ?? 0) - (a.t ?? 0) || (b.order ?? 0) - (a.order ?? 0);

/** 知らせの 1 行 → 席の状態 */
export function applySpeechLine(st, line, nowMs = Date.now()) {
  if (line.type === 'start') return { ...st, phase: 'watching', error: null };
  if (line.type === 'ping') return { ...st, lastPing: nowMs };
  if (line.type === 'end') return { ...st, phase: 'closed' };
  if (line.type !== 'speech') return st;
  if (!line.final) return { ...st, partial: { text: line.text, audio: line.audio, at: nowMs } };
  const item = { key: `s${line.seq}`, text: line.text, audio: line.audio, level: line.level_db, keep: true,
    t: line.start_ms ?? line.t_unix_ms ?? nowMs, order: nowMs, att: line.attribution ?? null };
  const finals = [item, ...(st.finals ?? []).filter((f) => f.key !== item.key)].sort(byTime).slice(0, MAX_FINALS);
  return { ...st, partial: null, finals };
}

/** recent の判定のうち、その席で捨てたもの → 確定の欄へ（流したものは知らせから来るので重ねない） */
export function applyDecisions(st, decisions, seat, nowMs = Date.now()) {
  const dropped = (decisions ?? []).filter((d) => String(d.channel) === String(seat) && !d.keep)
    .map((d) => ({ key: `d${d.id}`, text: d.text, audio: d.audio, level: d.level_db, keep: false,
      t: d.start_ms ?? d.t_unix_ms ?? nowMs, order: nowMs, att: d }));
  const have = new Set((st.finals ?? []).map((f) => f.key));
  const fresh = dropped.filter((d) => !have.has(d.key));
  if (!fresh.length) return st;
  return { ...st, finals: [...fresh, ...(st.finals ?? [])].sort(byTime).slice(0, MAX_FINALS) };
}

const clock = (t) => (t ? new Date(t).toLocaleTimeString('ja-JP', { hour12: false }) : '');

function SeatSpeech({ seat, st, thresholds }) {
  const PHASE = { connecting: 'つないでいます…', watching: '聞いています', closed: '補正サーバーが閉じました（もう一度「聞く」）', failed: 'つなげませんでした' };
  return (
    <div style={{ display: 'grid', gap: 6, minWidth: 0, alignContent: 'start' }} data-testid={`speech-seat-${seat}`}>
      <strong>P{seat}</strong>
      <div style={{ fontSize: 12, color: st.phase === 'watching' && !st.error ? '#9be7a1' : '#f0c36b' }}>
        {PHASE[st.phase] ?? st.phase}{st.error ? `（${st.error}）` : ''}
      </div>
      <div style={{ fontSize: 12, color: '#8a93a8' }}>途中</div>
      <div data-testid={`speech-partial-${seat}`}
        style={{ minHeight: 28, padding: '4px 8px', background: '#141a26', borderRadius: 6, fontSize: 16, overflowWrap: 'anywhere' }}>
        {st.partial ? <>{st.partial.text} <span style={{ fontSize: 11, color: '#8a93a8' }}>（{sourceText(st.partial.audio)}）</span></>
          : <span style={{ color: '#5c6478', fontSize: 13 }}>—</span>}
      </div>
      <div style={{ fontSize: 12, color: '#8a93a8' }}>確定</div>
      <div data-testid={`speech-finals-${seat}`} style={{ display: 'grid', gap: 4, maxHeight: 320, overflowY: 'auto' }}>
        {(st.finals ?? []).length === 0 ? <span style={{ color: '#5c6478', fontSize: 13 }}>まだありません</span> : null}
        {(st.finals ?? []).map((f) => (
          <div key={f.key} data-testid={`speech-final-${seat}`} data-keep={f.keep ? '1' : '0'}
            style={{ display: 'grid', gap: 1, padding: '4px 8px', borderRadius: 6, background: f.keep ? '#17241b' : '#1b1d22', color: f.keep ? undefined : '#7d8494' }}>
            <span style={{ fontSize: 15, overflowWrap: 'anywhere', textDecoration: f.keep ? 'none' : 'line-through' }}>{f.text}</span>
            <span style={{ fontSize: 11, color: '#8a93a8' }}>
              {clock(f.t)}・{sourceText(f.audio)}・{db(f.level)}・{judgeText(f.att, thresholds)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SpeechPanel({ fetchImpl = null, pollMs = POLL_MS }) {
  const [on, setOn] = useState(false);
  const [seats, setSeats] = useState({ 1: { phase: 'idle', finals: [] }, 2: { phase: 'idle', finals: [] } });
  const [thresholds, setThresholds] = useState(null);
  const ctrl = useRef(null);
  const f = fetchImpl ?? (typeof fetch === 'function' && !navigator.userAgent.includes('jsdom') ? fetch : null);
  const setSeat = (seat, fn) => setSeats((s) => ({ ...s, [seat]: fn(s[seat]) }));

  useEffect(() => {
    if (!on || !f) return undefined;
    const ac = new AbortController();
    ctrl.current = ac;
    const watch = async (seat) => {
      setSeat(seat, (st) => ({ ...st, phase: 'connecting', error: null }));
      try {
        const r = await f(liveUrl(`/live/speech/watch?channel=${seat}`), { signal: ac.signal });
        if (!r.ok || !r.body) throw new Error(`HTTP ${r.status}`);
        const reader = r.body.getReader();
        const dec = new TextDecoder();
        let buf = '';
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const [lines, rest] = splitLines(buf);
          buf = rest;
          lines.forEach((l) => { try { const line = JSON.parse(l); setSeat(seat, (st) => applySpeechLine(st, line)); } catch { /* 壊れた行は飛ばす */ } });
        }
        setSeat(seat, (st) => ({ ...st, phase: 'closed' }));
      } catch (e) {
        if (!ac.signal.aborted) setSeat(seat, (st) => ({ ...st, phase: 'failed', error: e.message }));
      }
    };
    const poll = async () => {
      try {
        const r = await f(liveUrl('/live/speech/recent?channel=1'), { signal: ac.signal });
        if (!r.ok) return;
        const body = await r.json();
        setThresholds({ margin_db: body.margin_db, min_level_db: body.min_level_db });
        setSeats((s) => ({ 1: applyDecisions(s[1], body.decisions, 1), 2: applyDecisions(s[2], body.decisions, 2) }));
      } catch { /* 次の回に */ }
    };
    watch(1);
    watch(2);
    poll();
    const timer = setInterval(poll, pollMs);
    return () => { clearInterval(timer); ac.abort(); };
  }, [on, f, pollMs]);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="card" data-testid="speech-panel" style={{ display: 'grid', gap: 8, minWidth: 0 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <h3 className="card-title" style={{ margin: 0 }}>音声認識</h3>
        <button type="button" className="btn" onClick={() => setOn((v) => !v)} data-testid="speech-toggle">{on ? '止める' : '聞く'}</button>
        <span style={{ fontSize: 12, color: '#8a93a8' }}>{thresholdText(thresholds)}</span>
      </div>
      <p style={{ margin: 0, fontSize: 12, color: '#8a93a8' }}>
        iPhone（TetherCam）が聞いた文字です。同席は OBS の話者ごとのトラック、リモートは iPhone のマイク。2 台が同じ声を聞いたら大きいほうの席にし、
        漏れ込み・小さすぎる声は捨てます（捨てたものは灰色）。
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
        <SeatSpeech seat={1} st={seats[1]} thresholds={thresholds ?? {}} />
        <SeatSpeech seat={2} st={seats[2]} thresholds={thresholds ?? {}} />
      </div>
    </section>
  );
}
